import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import lcaClient, {
  downloadLcaTemplate,
  importErrorSummary,
  uploadErrorMessage,
} from '../../services/lcaClient';
import { useSite } from '../../context/SiteContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, Database, FileDown, Upload, Trash2, Check, X, Edit2,
  Lock, Building2
} from 'lucide-react';
import { motion } from 'framer-motion';

const api = lcaClient;

const categoryOptions = [
  { value: 'bom', label: 'Materials (BOM)', color: 'bg-blue-500/20 text-blue-400' },
  { value: 'energy', label: 'Energy', color: 'bg-yellow-500/20 text-yellow-400' },
  { value: 'transport', label: 'Transport', color: 'bg-green-500/20 text-green-400' },
  { value: 'process', label: 'Process', color: 'bg-purple-500/20 text-purple-400' },
  // Gate-to-Grave categories
  { value: 'use_phase', label: 'Use Phase', color: 'bg-cyan-500/20 text-cyan-400' },
  { value: 'maintenance', label: 'Maintenance', color: 'bg-orange-500/20 text-orange-400' },
  { value: 'eol', label: 'End-of-Life', color: 'bg-rose-500/20 text-rose-400' }
];

export default function DatasetLibrary({ onBack, study, corporateMode = false }) {
  const { activeSiteId: globalSiteId } = useSite();
  // Study scope wins over the global site picker (matches LCADashboard).
  // In corporateMode, ignore both — show tenant-wide datasets across all sites
  // so users can curate the library before scoping it to a project.
  const activeSiteId = corporateMode ? null : (study?.site_id || globalSiteId);
  const [datasets, setDatasets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  // Reference-library browse mode (shared, read-only BAFU catalogue) —
  // server-side paginated search instead of the client-filtered tenant list.
  const refMode = sourceFilter === 'reference';
  // When browsing "All Sources", a search should also surface matches from the
  // shared BAFU reference catalogue (server-side) alongside the tenant datasets.
  // Gated on a non-empty search so the default (unsearched) view stays fast and
  // doesn't pull all ~11k reference rows.
  const showReference = refMode || (sourceFilter === 'all' && searchTerm.trim() !== '');
  const [refItems, setRefItems] = useState([]);
  const [refTotal, setRefTotal] = useState(0);
  const [refPage, setRefPage] = useState(1);
  const [refLoading, setRefLoading] = useState(false);
  const REF_PAGE_SIZE = 25;
  const [uploading, setUploading] = useState(false);
  const [newRow, setNewRow] = useState(null);
  const [editingRowId, setEditingRowId] = useState(null);
  const [editingData, setEditingData] = useState({});
  const fileInputRef = useRef(null);

  // Product-aware filter: when a product is picked, narrow datasets to ones
  // relevant for that product. If the product's category text matches one of
  // the controlled inventory-category slugs (bom/energy/transport/...), we
  // hard-filter on dataset.category. Otherwise we do a substring search on
  // dataset.name / dataset.category text using the product's category +
  // product name as hints. Frontend-only — no API change needed.
  const [products, setProducts] = useState([]);
  const [productFilter, setProductFilter] = useState('all');
  useEffect(() => {
    // Only load product master in corporateMode (it's the place where users
    // browse datasets BEFORE creating a project). Skip otherwise to keep the
    // study-scoped flow uncluttered.
    if (!corporateMode) return;
    const token = localStorage.getItem('token');
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/products`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => setProducts(r.data?.products || []))
      .catch(() => {});
  }, [corporateMode]);

  const selectedProduct = productFilter !== 'all'
    ? products.find((p) => p.id === productFilter)
    : null;

  useEffect(() => {
    fetchDatasets();
  }, [activeSiteId]);

  // Debounced server-side reference search.
  useEffect(() => {
    if (!showReference) return;
    setRefLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get('/reference/datasets', {
          params: {
            q: searchTerm || undefined,
            category: categoryFilter !== 'all' ? categoryFilter : undefined,
            page: refPage,
            page_size: REF_PAGE_SIZE,
          },
        });
        setRefItems(data.items || []);
        setRefTotal(data.total || 0);
      } catch (e) {
        toast.error('Failed to load reference library');
      } finally {
        setRefLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [showReference, searchTerm, categoryFilter, refPage]);

  useEffect(() => {
    setRefPage(1);
  }, [searchTerm, categoryFilter, showReference]);

  const fetchDatasets = async () => {
    setLoading(true);
    try {
      const params = activeSiteId ? { site_id: activeSiteId } : undefined;
      const response = await api.get('/datasets', { params });
      setDatasets(response.data);
    } catch (error) {
      toast.error('Failed to load datasets');
    } finally {
      setLoading(false);
    }
  };

  const getEmptyRow = () => ({
    name: '',
    category: 'bom',
    unit: '',
    emission_factor: '',
    geography: 'Global',
    source: ''
  });

  const addNewRow = () => {
    setNewRow(getEmptyRow());
  };

  const cancelNewRow = () => {
    setNewRow(null);
  };

  const saveNewRow = async () => {
    if (!newRow.name || !newRow.unit || !newRow.emission_factor) {
      toast.error('Name, Unit, and Emission Factor are required');
      return;
    }

    try {
      const response = await api.post('/datasets/bulk', {
        name: newRow.name,
        category: newRow.category,
        unit: newRow.unit,
        emission_factor: parseFloat(newRow.emission_factor),
        geography: newRow.geography || 'Global',
        source: newRow.source || 'User uploaded',
        ...(activeSiteId ? { site_id: activeSiteId } : {}),
      });
      setDatasets([...datasets, response.data]);
      setNewRow(null);
      toast.success('Dataset added');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add dataset');
    }
  };

  const startEditing = (item) => {
    setEditingRowId(item.id);
    setEditingData({
      name: item.name,
      category: item.category,
      unit: item.unit,
      emission_factor: item.emission_factor,
      geography: item.geography,
      source: item.source
    });
  };

  const cancelEditing = () => {
    setEditingRowId(null);
    setEditingData({});
  };

  const saveEditing = async (itemId) => {
    if (!editingData.name || !editingData.unit || !editingData.emission_factor) {
      toast.error('Name, Unit, and Emission Factor are required');
      return;
    }

    try {
      const response = await api.put(`/datasets/${itemId}`, {
        name: editingData.name,
        category: editingData.category,
        unit: editingData.unit,
        emission_factor: parseFloat(editingData.emission_factor),
        geography: editingData.geography || 'Global',
        source: editingData.source || 'User uploaded'
      });
      setDatasets(datasets.map(d => d.id === itemId ? response.data : d));
      setEditingRowId(null);
      setEditingData({});
      toast.success('Dataset updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update dataset');
    }
  };

  const deleteDataset = async (itemId) => {
    try {
      await api.delete(`/datasets/${itemId}`);
      setDatasets(datasets.filter(d => d.id !== itemId));
      toast.success('Dataset deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete dataset');
    }
  };

  const downloadTemplate = async () => {
    try {
      await downloadLcaTemplate('/datasets/template', 'dataset_template.csv');
      toast.success('Template downloaded');
    } catch (error) {
      toast.error(error.message || 'Failed to download template');
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const url = activeSiteId
        ? `/datasets/upload?site_id=${encodeURIComponent(activeSiteId)}`
        : '/datasets/upload';
      const response = await api.post(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const { items_created: created = 0, errors = [] } = response.data || {};
      if (errors.length > 0) {
        // The import is all-or-nothing, so nothing was written. Show the
        // actual row messages rather than just a count.
        // whiteSpace preserves the newlines importErrorSummary puts between
        // row messages; without it they run together on one line.
        toast.error(importErrorSummary(errors), {
          description: 'Nothing was imported — fix the file and try again.',
          style: { whiteSpace: 'pre-line' },
        });
      } else if (created > 0) {
        toast.success(`${created} datasets imported`);
        fetchDatasets();
      } else {
        toast.warning('No rows found in that file — nothing was imported');
      }
    } catch (error) {
      toast.error(uploadErrorMessage(error));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Inventory category slugs recognized by the LCA engine. If a product's
  // category text matches one of these (case-insensitive), it acts as a hard
  // filter; otherwise we treat it as fuzzy text.
  const INVENTORY_CATEGORY_SLUGS = new Set(
    ['bom', 'energy', 'transport', 'process', 'use_phase', 'maintenance', 'eol']
  );

  const productHints = selectedProduct
    ? [selectedProduct.category, selectedProduct.name].filter(Boolean).map((s) => s.toLowerCase())
    : [];
  const productControlledCategory = selectedProduct?.category
    && INVENTORY_CATEGORY_SLUGS.has(String(selectedProduct.category).toLowerCase())
    ? String(selectedProduct.category).toLowerCase()
    : null;

  const filteredDatasets = datasets.filter(ds => {
    const matchesSearch = ds.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         ds.source.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || ds.category === categoryFilter;
    const matchesSource = sourceFilter === 'all' || ds.source_type === sourceFilter;
    if (!matchesSearch || !matchesCategory || !matchesSource) return false;

    if (!selectedProduct) return true;

    // Hard filter when product's category text matches a known LCA category.
    if (productControlledCategory) return ds.category === productControlledCategory;

    // Fuzzy: dataset must contain ANY of the product hints in name or category.
    const hay = `${ds.name || ''} ${ds.category || ''}`.toLowerCase();
    return productHints.some((h) => h && hay.includes(h));
  });

  const getCategoryBadge = (category) => {
    const cat = categoryOptions.find(c => c.value === category);
    return cat?.color || 'bg-gray-500/20 text-gray-400';
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="datasets-page">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => onBack?.()} data-testid="back-btn" className="text-slate-500 hover:text-sky-700 hover:bg-sky-50">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div>
                <h1 className="text-lg font-bold text-slate-900">Dataset Library</h1>
                <p className="text-xs text-slate-500">LCI Emission Factors</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* SitePicker is provided by SustainabilityHub */}
            <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-btn" className="border-slate-300 text-slate-700 hover:bg-slate-100">
              <FileDown className="h-4 w-4 mr-2" />
              Template
            </Button>
            <input type="file" ref={fileInputRef} accept=".csv" onChange={handleFileUpload} className="hidden" />
            <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="upload-csv-btn" className="bg-sky-600 hover:bg-sky-700 text-white">
              {uploading ? <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-current border-t-transparent" /> : <Upload className="h-4 w-4 mr-2" />}
              {uploading ? 'Uploading...' : 'Upload CSV'}
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Input
              placeholder="Search datasets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-4 border-slate-300 bg-white"
              data-testid="dataset-search-input"
            />
          </div>
          {corporateMode && products.length > 0 && (
            <Select value={productFilter} onValueChange={setProductFilter}>
              <SelectTrigger className="w-[200px] border-slate-300 bg-white" data-testid="product-filter-select">
                <SelectValue placeholder="Product" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All Products</SelectItem>
                {products.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}{p.category ? ` — ${p.category}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[180px] border-slate-300 bg-white" data-testid="category-filter-select">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All Categories</SelectItem>
              {categoryOptions.map(cat => (
                <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger className="w-[160px] border-slate-300 bg-white" data-testid="source-filter-select">
              <SelectValue placeholder="Source" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All Sources</SelectItem>
              <SelectItem value="system">System</SelectItem>
              <SelectItem value="user">User</SelectItem>
              <SelectItem value="reference">Reference (BAFU 2025)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {selectedProduct && (
          <div className="-mt-2 mb-4 text-xs text-slate-600 bg-teal-50 border border-teal-200 rounded px-3 py-2">
            Showing datasets relevant to <strong>{selectedProduct.name}</strong>
            {selectedProduct.category && (
              <> (<span className="italic">{selectedProduct.category}</span>)</>
            )}.
            {productControlledCategory
              ? <> Filter applied: <code className="bg-white px-1 rounded">category = {productControlledCategory}</code>.</>
              : ' Fuzzy match against dataset name & category. Adjust the Category dropdown for an exact LCA category filter.'}
          </div>
        )}

        {/* When combined with the tenant grid ("All Sources" + search), label
            the reference matches so it's clear they're a separate read-only set. */}
        {showReference && !refMode && (
          <p className="text-sm font-semibold text-slate-700 mt-6 mb-2">
            Reference library matches — BAFU 2025 <span className="font-normal text-muted-foreground">(read-only)</span>
          </p>
        )}

        {/* Reference library table (read-only, server-paginated) */}
        {showReference && (
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="p-0">
              {refLoading && refItems.length === 0 ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary mx-auto"></div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-muted/50 border-b border-border">
                        <th className="px-3 py-3 text-left font-semibold text-muted-foreground">Name</th>
                        <th className="px-3 py-3 text-left font-semibold text-muted-foreground w-32">Category</th>
                        <th className="px-3 py-3 text-left font-semibold text-muted-foreground w-20">Unit</th>
                        <th className="px-3 py-3 text-right font-semibold text-muted-foreground w-32">GWP100 (kg CO₂e)</th>
                        <th className="px-3 py-3 text-left font-semibold text-muted-foreground w-24">Geography</th>
                        <th className="px-3 py-3 text-left font-semibold text-muted-foreground">Source Category</th>
                      </tr>
                    </thead>
                    <tbody>
                      {refItems.map((ds) => (
                        <tr key={ds.id} className="border-b border-border/50 hover:bg-muted/30" data-testid={`ref-dataset-row-${ds.id}`}>
                          <td className="px-3 py-3 font-medium">
                            <div className="flex items-center gap-2">
                              <span>{ds.name}</span>
                              <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-700 bg-emerald-50">
                                {ds.library_name}:{ds.library_version}
                              </Badge>
                              {ds.is_obsolete && (
                                <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-600">Obsolete</Badge>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-3">
                            {ds.mapped_category ? (
                              <Badge className={getCategoryBadge(ds.mapped_category)} variant="secondary">
                                {ds.mapped_category.toUpperCase()}
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-[10px] text-slate-400">UNMAPPED</Badge>
                            )}
                          </td>
                          <td className="px-3 py-3 font-mono">{ds.unit}</td>
                          <td className="px-3 py-3 text-right font-mono">
                            {ds.gwp100 != null ? Number(ds.gwp100).toLocaleString(undefined, { maximumSignificantDigits: 5 }) : '—'}
                          </td>
                          <td className="px-3 py-3">{ds.geography_code || '—'}</td>
                          <td className="px-3 py-3 text-muted-foreground">{ds.source_category}</td>
                        </tr>
                      ))}
                      {refItems.length === 0 && (
                        <tr>
                          <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                            <Database className="h-8 w-8 mx-auto mb-2 opacity-50" />
                            <p>No reference datasets match your search.</p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {showReference && (
          <div className="flex items-center justify-between mt-3">
            <p className="text-xs text-muted-foreground">
              {refTotal.toLocaleString()} reference datasets · page {refPage} of {Math.max(1, Math.ceil(refTotal / REF_PAGE_SIZE))}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline" size="sm" disabled={refPage <= 1 || refLoading}
                onClick={() => setRefPage(refPage - 1)} data-testid="ref-prev-page"
              >
                Previous
              </Button>
              <Button
                variant="outline" size="sm"
                disabled={refPage >= Math.ceil(refTotal / REF_PAGE_SIZE) || refLoading}
                onClick={() => setRefPage(refPage + 1)} data-testid="ref-next-page"
              >
                Next
              </Button>
            </div>
          </div>
        )}

        {/* Dataset Table */}
        {!refMode && (
        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary mx-auto"></div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted/50 border-b border-border">
                      <th className="px-3 py-3 text-left font-semibold text-muted-foreground">Name *</th>
                      <th className="px-3 py-3 text-left font-semibold text-muted-foreground w-32">Category *</th>
                      <th className="px-3 py-3 text-left font-semibold text-muted-foreground w-20">Unit *</th>
                      <th className="px-3 py-3 text-right font-semibold text-muted-foreground w-32">EF (kg CO₂e) *</th>
                      <th className="px-3 py-3 text-left font-semibold text-muted-foreground w-24">Geography</th>
                      <th className="px-3 py-3 text-left font-semibold text-muted-foreground">Source</th>
                      <th className="px-3 py-3 text-center font-semibold text-muted-foreground w-24">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDatasets.map((ds, idx) => (
                      <tr key={ds.id} className="border-b border-border/50 hover:bg-muted/30" data-testid={`dataset-row-${ds.id}`}>
                        {editingRowId === ds.id ? (
                          // Editing mode
                          <>
                            <td className="px-2 py-2">
                              <Input
                                value={editingData.name}
                                onChange={(e) => setEditingData({...editingData, name: e.target.value})}
                                className="h-8 text-sm"
                              />
                            </td>
                            <td className="px-2 py-2">
                              <Select value={editingData.category} onValueChange={(v) => setEditingData({...editingData, category: v})}>
                                <SelectTrigger className="h-8 text-sm">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {categoryOptions.map(cat => (
                                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="px-2 py-2">
                              <Input
                                value={editingData.unit}
                                onChange={(e) => setEditingData({...editingData, unit: e.target.value})}
                                className="h-8 text-sm"
                              />
                            </td>
                            <td className="px-2 py-2">
                              <Input
                                type="number"
                                step="0.0001"
                                value={editingData.emission_factor}
                                onChange={(e) => setEditingData({...editingData, emission_factor: e.target.value})}
                                className="h-8 text-sm text-right"
                              />
                            </td>
                            <td className="px-2 py-2">
                              <Input
                                value={editingData.geography}
                                onChange={(e) => setEditingData({...editingData, geography: e.target.value})}
                                className="h-8 text-sm"
                              />
                            </td>
                            <td className="px-2 py-2">
                              <Input
                                value={editingData.source}
                                onChange={(e) => setEditingData({...editingData, source: e.target.value})}
                                className="h-8 text-sm"
                              />
                            </td>
                            <td className="px-2 py-2">
                              <div className="flex items-center justify-center gap-1">
                                <Button size="icon" variant="ghost" className="h-7 w-7 text-emerald-500" onClick={() => saveEditing(ds.id)}>
                                  <Check className="h-4 w-4" />
                                </Button>
                                <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground" onClick={cancelEditing}>
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </>
                        ) : (
                          // Display mode
                          <>
                            <td className="px-3 py-3 font-medium">
                              <div className="flex items-center gap-2">
                                <span>{ds.name}</span>
                                {ds.source_type === 'system' ? (
                                  <Badge variant="outline" className="text-[10px] border-slate-300 text-slate-500">
                                    <Lock className="h-3 w-3 mr-1" />
                                    System
                                  </Badge>
                                ) : (
                                  ds.site_name && (
                                    <Badge variant="outline" className="text-[10px] border-slate-300 text-slate-600">
                                      <Building2 className="h-3 w-3 mr-1" />
                                      {ds.site_name}
                                    </Badge>
                                  )
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-3">
                              <Badge className={getCategoryBadge(ds.category)} variant="secondary">
                                {ds.category.toUpperCase()}
                              </Badge>
                            </td>
                            <td className="px-3 py-3 font-mono">{ds.unit}</td>
                            <td className="px-3 py-3 text-right font-mono">{ds.emission_factor}</td>
                            <td className="px-3 py-3">{ds.geography}</td>
                            <td className="px-3 py-3 text-muted-foreground">{ds.source}</td>
                            <td className="px-3 py-3">
                              {ds.editable ? (
                                <div className="flex items-center justify-center gap-1">
                                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => startEditing(ds)} data-testid={`edit-${ds.id}`}>
                                    <Edit2 className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => deleteDataset(ds.id)} data-testid={`delete-${ds.id}`}>
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              ) : (
                                <div className="text-center text-xs text-slate-400">—</div>
                              )}
                            </td>
                          </>
                        )}
                      </tr>
                    ))}

                    {/* New Row Input */}
                    {newRow && (
                      <tr className="bg-primary/5 border-b border-primary/20">
                        <td className="px-2 py-2">
                          <Input
                            value={newRow.name}
                            onChange={(e) => setNewRow({...newRow, name: e.target.value})}
                            placeholder="Dataset name"
                            className="h-8 text-sm"
                            autoFocus
                            data-testid="new-row-name"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <Select value={newRow.category} onValueChange={(v) => setNewRow({...newRow, category: v})}>
                            <SelectTrigger className="h-8 text-sm" data-testid="new-row-category">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {categoryOptions.map(cat => (
                                <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-2 py-2">
                          <Input
                            value={newRow.unit}
                            onChange={(e) => setNewRow({...newRow, unit: e.target.value})}
                            placeholder="kg"
                            className="h-8 text-sm"
                            data-testid="new-row-unit"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <Input
                            type="number"
                            step="0.0001"
                            value={newRow.emission_factor}
                            onChange={(e) => setNewRow({...newRow, emission_factor: e.target.value})}
                            placeholder="0.0"
                            className="h-8 text-sm text-right"
                            data-testid="new-row-ef"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <Input
                            value={newRow.geography}
                            onChange={(e) => setNewRow({...newRow, geography: e.target.value})}
                            placeholder="Global"
                            className="h-8 text-sm"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <Input
                            value={newRow.source}
                            onChange={(e) => setNewRow({...newRow, source: e.target.value})}
                            placeholder="Source"
                            className="h-8 text-sm"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <div className="flex items-center justify-center gap-1">
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-emerald-500" onClick={saveNewRow} data-testid="save-new-row">
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground" onClick={cancelNewRow}>
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )}

                    {/* Empty State */}
                    {filteredDatasets.length === 0 && !newRow && (
                      <tr>
                        <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                          <Database className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          <p>{datasets.length === 0 ? 'No datasets available. Add custom datasets or upload a CSV.' : 'No datasets match your search.'}</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {/* Add Row Button (tenant/user datasets only — reference is read-only) */}
        {!newRow && !refMode && (
          <Button variant="outline" className="w-full mt-3 border-dashed" onClick={addNewRow} data-testid="add-row-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Row
          </Button>
        )}

        {!refMode && (
          <p className="text-xs text-muted-foreground mt-4 text-center">
            {filteredDatasets.length} of {datasets.length} datasets shown
          </p>
        )}
      </main>
    </div>
  );
}
